import humanizeString from 'humanize-string'
import sortBy from 'lodash/sortBy'
import { getValue } from 'mobx-async'
import slugify from 'slugify'

import {
  BookingCheckInIssue,
  BookingStatus,
  CheckInInstructionsStatus,
  LeadSource,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  badgeColorByLeadSource,
  badgeColorsByRiskVerificationStatus,
  badgeLabelByRiskVerificationStatus,
  capitalizeEachFirstLetter,
  leadSourceLabel,
  mainColors,
  OptionsProps,
  semanticColors,
} from '@avantstay/backoffice-core'

import {
  colorsByBookingStatusType,
  colorsByPaymentAuditStatusType,
} from '@new/__global/constants/color'
import { BookingEarlyCheckInStatus } from '@new/__global/types/booking.types'
import { PropertyValueAddedServicesStore } from '@new/domains/properties/stores/PropertyValueAddedServicesStore'

import { AuditPaymentStatus } from '../types/paymentAudit.types'

const checkInInstructionsIssuesCustomLabels: Record<string, string> = {
  [BookingCheckInIssue.missingIdVerification]: 'IDV missing',
  [BookingCheckInIssue.idVerificationExpired]: 'IDV expired',
  [BookingCheckInIssue.missingBookingContract]: 'Contract missing',
  [BookingCheckInIssue.idVerificationNeedsReview]: 'IDV needs review',
  [BookingCheckInIssue.missingAccessInstruction]: 'CI instructions missing',
  [BookingCheckInIssue.entryInfoOnHold]: 'CI instructions on hold',
}

const checkInInstructionsIssuesOrder = [
  BookingCheckInIssue.missingIdVerification,
  BookingCheckInIssue.idVerificationExpired,
  BookingCheckInIssue.missingBookingContract,
  BookingCheckInIssue.idVerificationNeedsReview,
  BookingCheckInIssue.missingAccessInstruction,
  BookingCheckInIssue.entryInfoOnHold,
  BookingCheckInIssue.missingBookingPayment,
  BookingCheckInIssue.nonBookedStatus,
]

const checkInInstructionsStatusColors: Record<CheckInInstructionsStatus, string> = {
  [CheckInInstructionsStatus.blocked]: semanticColors.warningDark,
  [CheckInInstructionsStatus.deliveredAuto]: semanticColors.successMedium,
  [CheckInInstructionsStatus.deliveredManually]: semanticColors.successMedium,
  [CheckInInstructionsStatus.deliveryError]: semanticColors.failureMedium,
  [CheckInInstructionsStatus.scheduled]: mainColors.accentMedium,
  [CheckInInstructionsStatus.autoReleased]: mainColors.neutralMedium,
}

const checkInInstructionsStatusCustomLabels: Record<string, string> = {
  [CheckInInstructionsStatus.deliveredAuto]: 'Auto delivered',
  [CheckInInstructionsStatus.deliveredManually]: 'Manually delivered',
}

const checkInInstructionsStatusOrder = [
  CheckInInstructionsStatus.blocked,
  CheckInInstructionsStatus.deliveryError,
  CheckInInstructionsStatus.scheduled,
  CheckInInstructionsStatus.deliveredAuto,
  CheckInInstructionsStatus.deliveredManually,
]

const earlyCheckInColor = {
  [BookingEarlyCheckInStatus.Approved]: mainColors.accentMedium,
  [BookingEarlyCheckInStatus.Cancelled]: semanticColors.failureMedium,
  [BookingEarlyCheckInStatus.Expired]: semanticColors.warningDark,
  [BookingEarlyCheckInStatus.Rejected]: semanticColors.failureMedium,
  [BookingEarlyCheckInStatus.Requested]: mainColors.neutralExtraDark,
}

const earlyCheckInOrder = {
  [BookingEarlyCheckInStatus.Approved]: 2,
  [BookingEarlyCheckInStatus.Cancelled]: 4,
  [BookingEarlyCheckInStatus.Expired]: 1,
  [BookingEarlyCheckInStatus.Rejected]: 3,
  [BookingEarlyCheckInStatus.Requested]: 0,
}

export const bookingStatusFilterOptions: OptionsProps<BookingStatus>[] = Array.from(
  Object.values(BookingStatus),
  status => ({
    dot: colorsByBookingStatusType[status]?.color ?? 'transparent',
    label: capitalizeEachFirstLetter(humanizeString(status)),
    value: status,
  }),
)

export const useVASFilterOptions = () => {
  const services = getValue(PropertyValueAddedServicesStore.getServices)
  const options = services?.map(service => ({ label: service.name, value: service.id })) ?? []

  return { options }
}

export const paymentAuditStatusFilterOptions: OptionsProps<AuditPaymentStatus>[] = Array.from(
  Object.values(AuditPaymentStatus),
  status => ({
    dot: colorsByPaymentAuditStatusType[status]?.color ?? 'transparent',
    label: capitalizeEachFirstLetter(status).replace(/_/g, ' '),
    value: status,
  }),
)

export const checkInInstructionsIssuesFilterOptions: OptionsProps<BookingCheckInIssue>[] =
  Array.from(
    Object.values(BookingCheckInIssue).sort(
      (a, b) =>
        checkInInstructionsIssuesOrder.findIndex(value => value === a) -
        checkInInstructionsIssuesOrder.findIndex(value => value === b),
    ),
    value => ({
      hasFullWidth: true,
      label:
        checkInInstructionsIssuesCustomLabels[value] ??
        capitalizeEachFirstLetter(value).replace(/_/g, ' '),
      value,
    }),
  )

export const checkInInstructionsStatusFilterOptions: OptionsProps<CheckInInstructionsStatus>[] =
  Array.from(
    Object.values(CheckInInstructionsStatus).sort(
      (a, b) =>
        checkInInstructionsStatusOrder.findIndex(value => value === a) -
        checkInInstructionsStatusOrder.findIndex(value => value === b),
    ),
    value => ({
      dot: checkInInstructionsStatusColors[value] ?? 'transparent',
      hasFullWidth: true,
      label:
        checkInInstructionsStatusCustomLabels[value] ??
        capitalizeEachFirstLetter(value).replace(/_/g, ' '),
      value,
    }),
  )

export const earlyCheckInFilterOptions: OptionsProps<BookingEarlyCheckInStatus>[] = Array.from(
  Object.values(BookingEarlyCheckInStatus).sort((statusA, statusB) => {
    return earlyCheckInOrder[statusA] - earlyCheckInOrder[statusB]
  }),
  value => ({
    dot: earlyCheckInColor[value] ?? 'transparent',
    label: capitalizeEachFirstLetter(value).replace(/_/g, ' '),
    value,
  }),
)

export const leadSourceFilterOptions: OptionsProps<string>[] = sortBy(
  Object.values(LeadSource)
    .filter(value => value !== LeadSource.homeAway)
    .map(value => ({ label: leadSourceLabel[value] ?? humanizeString(value), value })),
  ['label', 'asc'],
)

export const getRiskVerificationStatusOption = (optionValue: string) => {
  return {
    label:
      badgeLabelByRiskVerificationStatus[
        optionValue as keyof typeof badgeLabelByRiskVerificationStatus
      ],
    value: optionValue,
    color:
      badgeColorsByRiskVerificationStatus[
        optionValue as keyof typeof badgeLabelByRiskVerificationStatus
      ],
  }
}

export const leadSourceFilterOptionsWithColors: OptionsProps<string>[] = sortBy(
  Object.values(LeadSource)
    .filter(value => value !== LeadSource.homeAway)
    .map(value => ({
      label: leadSourceLabel[value] ?? humanizeString(value),
      value,
      labelStyle: {
        ...badgeColorByLeadSource[value],
        border: `2px solid ${badgeColorByLeadSource[value]?.color}`,
        fontFamily: 'Roboto',
        fontWeight: '500',
        fontSize: '13px',
      },
    })),
  ['label', 'asc'],
)

export const AllRegions = {
  label: 'All Markets',
  value: slugify('All Markets', { lower: true }),
}
